import React, { useState } from 'react'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import robotoRegularBase64 from '../../assets/fonts/Roboto-Regular-base64'
import styles from './DownloadSmet.module.css'
import { ClaimResponseType, EstablishmentsType } from '../../redux/actions/types'
import Select from 'react-select'
import { useMediaQuery } from 'react-responsive'
import { addImageWithAspectRatio } from '../../utils/addImageWithAspectRatio'
import close from '../../assets/icons/close.svg'
import {
  Document,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  WidthType,
  AlignmentType,
} from 'docx'

interface DownloadSmetProps {
  data: ClaimResponseType[]
  selectedEstablismentForSmet?: EstablishmentsType | undefined
}

interface FormattedDataItem {
  Дата: string
  Клиент: string
  Зал: string
  'Еда и напитки': number
  'Алкогольные напитки': number
  Сервис: number
  'Пробковый сбор': number
  'Аренда площадки': number
  Всего: number
}

const formatDate = (isoDate: string | undefined) => {
  if (!isoDate) return ''
  const date = new Date(isoDate)
  const year = date.getUTCFullYear()
  const month = `${date.getUTCMonth() + 1}`.padStart(2, '0')
  const day = `${date.getUTCDate()}`.padStart(2, '0')
  return `${year}/${month}/${day}`
}

const DownloadSmet: React.FC<DownloadSmetProps> = ({ data, selectedEstablismentForSmet }) => {
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' })
  const [selectedFormat, setSelectedFormat] = useState<'pdf' | 'excel' | 'word' | null>(null)
  const [showModal, setShowModal] = useState(false)

  const getLogoUrl = () => {
    if (!selectedEstablismentForSmet?.logo) return null
    return `${process.env.REACT_APP_BASE_URL_IMAGES}${selectedEstablismentForSmet.logo}`
  }

  const getFormattedData = (): FormattedDataItem[] => {
    return data.map((item) => {
      const orderDetails = item?.order?.orderDetails || []

      const foodTotal = orderDetails
        .filter((product) => product?.category?.type === 'food')
        .reduce((sum, product) => sum + (product?.quantity || 0) * (product?.price || 0), 0)

      const alcoholTotal = orderDetails
        .filter((product) => product?.category?.type === 'alcohol')
        .reduce((sum, product) => sum + (product?.quantity || 0) * (product?.price || 0), 0)

      const serviceTotal = (foodTotal * (item.serviceFeePercent || 0)) / 100

      const corkFeeTotal =
        item.corkFeeAmount ||
        (item.corkFeePercent ? Math.round((alcoholTotal * item.corkFeePercent) / 100) : 0)

      const rentTotal = item?.place?.establishment?.rentCost || 0

      const total = foodTotal + alcoholTotal + serviceTotal + corkFeeTotal + rentTotal

      return {
        Дата: `${formatDate(item?.startDate)} - ${formatDate(item?.endDate)}`,
        Клиент: item.client?.fullName || item?.client?.organizationName || 'Неизвестный клиент',
        Зал: item?.place?.name || '',
        'Еда и напитки': foodTotal,
        'Алкогольные напитки': alcoholTotal,
        Сервис: serviceTotal,
        'Пробковый сбор': corkFeeTotal,
        'Аренда площадки': rentTotal,
        Всего: total,
      }
    })
  }

  const getTotals = (): Record<string, number> => {
    const formattedData = getFormattedData()
    const totals: Record<string, number> = {}

    if (formattedData.length === 0) return totals

    Object.keys(formattedData[0]).forEach((key) => {
      if (typeof formattedData[0][key as keyof FormattedDataItem] === 'number') {
        totals[key] = 0
      }
    })

    formattedData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (typeof row[key as keyof FormattedDataItem] === 'number') {
          totals[key] = (totals[key] || 0) + (row[key as keyof FormattedDataItem] as number)
        }
      })
    })

    return totals
  }

  const downloadPDF = async () => {
    const doc = new jsPDF()
    doc.addFileToVFS('Roboto-Regular.ttf', robotoRegularBase64)
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal')
    doc.setFont('Roboto', 'normal')

    const margin = 14
    let currentY = margin

    const companyName = selectedEstablismentForSmet?.name || ''
    if (companyName) {
      doc.setFontSize(12)
      doc.setTextColor(100)
      doc.text(companyName, margin, currentY)
      currentY += 10
    }

    doc.setFontSize(16)
    doc.setTextColor(0)
    doc.text('Смета заказа', margin, currentY)
    currentY += 15

    const logoUrl = getLogoUrl()
    if (logoUrl) {
      try {
        const response = await fetch(logoUrl)
        if (response.ok) {
          const blob = await response.blob()
          const imgData = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.onerror = reject
            reader.readAsDataURL(blob)
          })

          await addImageWithAspectRatio(doc, imgData, 150, 10, 40, 40, 'JPEG')
        }
      } catch (error) {
        console.error('Ошибка загрузки логотипа:', error)
      }
    }

    const tableData = getFormattedData()
    const totals = getTotals()

    const tableHead = [
      [
        'Дата',
        'Клиент',
        'Зал',
        'Еда и напитки',
        'Алкогольные напитки',
        'Сервис',
        'Пробковый сбор',
        'Аренда площадки',
        'Всего',
      ],
    ]

    const tableBody = tableData.map((obj) => Object.values(obj))

    const tableFoot = [
      [
        'Итого:',
        '',
        '',
        totals['Еда и напитки'],
        totals['Алкогольные напитки'],
        totals['Сервис'],
        totals['Пробковый сбор'],
        totals['Аренда площадки'],
        totals['Всего'],
      ],
    ]

    autoTable(doc, {
      head: tableHead,
      body: tableBody,
      foot: tableFoot,
      startY: currentY,
      margin: { top: 10 },
      theme: 'grid',
      styles: {
        font: 'Roboto',
        fontSize: 8,
      },
      headStyles: {
        font: 'Roboto',
        fillColor: [209, 209, 209],
        textColor: 0,
        fontSize: 8,
        fontStyle: 'normal',
      },
      footStyles: {
        font: 'Roboto',
        fillColor: [240, 240, 240],
        textColor: 0,
        fontSize: 8,
        fontStyle: 'normal',
      },
      willDrawCell: (data) => {
        if (data.section === 'head' || data.section === 'foot') {
          doc.setFont('Roboto', 'normal')
        }
      },
    })

    doc.save('order_data.pdf')
  }

  const downloadExcel = async () => {
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('Смета')

    const formattedData = getFormattedData()
    const totals = getTotals()

    const headers = [
      'Дата',
      'Клиент',
      'Зал',
      'Еда и напитки',
      'Алкогольные напитки',
      'Сервис',
      'Пробковый сбор',
      'Аренда площадки',
      'Всего',
    ]
    worksheet.addRow(headers)

    formattedData.forEach((row) => {
      worksheet.addRow(headers.map((header) => (row as any)[header]))
    })

    worksheet.addRow(['', '', '', '', '', '', '', '', ''])

    const totalRow = worksheet.addRow([
      'Итого:',
      '',
      '',
      totals['Еда и напитки'],
      totals['Алкогольные напитки'],
      totals['Сервис'],
      totals['Пробковый сбор'],
      totals['Аренда площадки'],
      totals['Всего'],
    ])
    totalRow.font = { bold: true }
    totalRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF0F0F0' },
    }

    worksheet.columns = [
      { width: 23 },
      { width: 30 },
      { width: 33 },
      { width: 18 },
      { width: 20 },
      { width: 12 },
      { width: 15 },
      { width: 20 },
      { width: 14 },
    ]

    worksheet.eachRow((row, rowNumber) => {
      row.eachCell((cell, colNumber) => {
        cell.alignment = { vertical: 'middle', horizontal: 'center' }
        if (rowNumber === 1) {
          cell.font = { bold: true }
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD1D1D1' },
          }
        }
      })
    })

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    saveAs(blob, 'order_data.xlsx')
  }

  const downloadWord = async () => {
    const formattedData = getFormattedData()
    const totals = getTotals()
    const companyName = selectedEstablismentForSmet?.name || ''

    const children: (Paragraph | Table)[] = []

    if (companyName) {
      children.push(
        new Paragraph({
          text: companyName,
          spacing: { after: 100 },
        }),
      )
    }

    children.push(
      new Paragraph({
        text: 'Смета заказа',
        heading: 'Heading1',
        spacing: { after: 300 },
        alignment: AlignmentType.CENTER,
      }),
    )

    const headers = [
      'Дата',
      'Клиент',
      'Зал',
      'Еда и напитки',
      'Алкогольные напитки',
      'Сервис',
      'Пробковый сбор',
      'Аренда площадки',
      'Всего',
    ]

    const tableRows: TableRow[] = [
      new TableRow({
        children: headers.map(
          (header) =>
            new TableCell({
              children: [
                new Paragraph({
                  text: header,
                  run: { bold: true },
                  alignment: AlignmentType.CENTER,
                }),
              ],
            }),
        ),
      }),
      ...formattedData.map(
        (row) =>
          new TableRow({
            children: headers.map((header) => {
              const value = row[header as keyof FormattedDataItem]
              const displayValue = typeof value === 'number' ? `${value} руб.` : String(value || '')
              return new TableCell({
                children: [
                  new Paragraph({
                    text: displayValue,
                    alignment: typeof value === 'number' ? AlignmentType.RIGHT : AlignmentType.LEFT,
                  }),
                ],
              })
            }),
          }),
      ),
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                text: 'Итого:',
                run: { bold: true },
              }),
            ],
          }),
          new TableCell({
            children: [new Paragraph('')],
          }),
          new TableCell({
            children: [new Paragraph('')],
          }),
          ...headers.slice(3).map((header) => {
            const value = totals[header as keyof typeof totals] || 0
            return new TableCell({
              children: [
                new Paragraph({
                  text: `${value} руб.`,
                  run: { bold: true },
                  alignment: AlignmentType.RIGHT,
                }),
              ],
            })
          }),
        ],
      }),
    ]

    children.push(
      new Table({
        rows: tableRows,
        width: { size: 100, type: WidthType.PERCENTAGE },
      }),
    )

    const doc = new Document({
      sections: [
        {
          children,
        },
      ],
    })

    Packer.toBlob(doc).then((blob: Blob) => {
      saveAs(blob, 'order_data.docx')
    })
  }

  const options = [
    { value: 'pdf', label: 'PDF' },
    { value: 'excel', label: 'Excel' },
    { value: 'word', label: 'Word' },
  ]

  const handleFormatChange = (selected: any) => {
    setSelectedFormat(selected.value)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedFormat(null)
  }

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleCloseModal()
    }
  }

  const tableData = getFormattedData()
  const totals = getTotals()

  return (
    <div className={styles.modalWrapper}>
      <div className={styles.selectBlock}>
        <Select
          className={styles.selectInput}
          options={options}
          placeholder='Выгрузить отчет'
          onChange={handleFormatChange}
          classNamePrefix='custom-select'
          styles={{
            control: (provided) => ({
              ...provided,
              border: 'none',
              boxShadow: 'none',
              minHeight: 'auto',
              backgroundColor: 'transparent',
            }),
            menu: (provided) => ({
              ...provided,
              right: 0,
              overflow: 'auto',
              height: '70px',
            }),
            menuList: (provided) => ({
              ...provided,
              height: '100%',
              maxHeight: '150px',
              overflowY: 'auto',
            }),
            input: (provided) => ({
              ...provided,
              margin: 0,
              padding: 0,
              height: isMobile ? 40 : 24,
            }),
            placeholder: (provided) => ({
              ...provided,
              fontSize: 13,
            }),
            valueContainer: (provided) => ({
              ...provided,
              padding: 0,
            }),
            dropdownIndicator: (provided) => ({
              ...provided,
              padding: 0,
            }),
            clearIndicator: (provided) => ({
              ...provided,
              padding: 0,
            }),
            indicatorSeparator: () => ({
              display: 'none',
            }),
          }}
        />
      </div>

      {showModal && (
        <div className={styles.modalOverlay} onClick={handleOverlayClick}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Смета</h3>
              <img
                src={close}
                alt='close'
                className={styles.closeIcon}
                onClick={handleCloseModal}
              />
            </div>
            <div className={styles.scrollableContent}>
              <div className={styles.tableWrapper}>
                <table className={styles.previewTable}>
                  <thead>
                    <tr>
                      {Object.keys(tableData[0]).map((header, idx) => (
                        <th key={idx}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((row, idx) => (
                      <tr key={idx}>
                        {Object.values(row).map((value, i) => (
                          <td key={i}>{value}</td>
                        ))}
                      </tr>
                    ))}
                    <tr className={styles.totalRow}>
                      <td>Итого:</td>
                      <td></td>
                      <td></td>
                      {Object.values(totals).map((value, i) => (
                        <td key={i}>{value}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            {selectedFormat && (
              <div className={styles.buttonBlock}>
                <button
                  onClick={
                    selectedFormat === 'pdf'
                      ? downloadPDF
                      : selectedFormat === 'excel'
                      ? downloadExcel
                      : downloadWord
                  }
                  className={styles.downloadBtn}
                >
                  Скачать {selectedFormat?.toUpperCase()}
                </button>
                <button onClick={handleCloseModal} className={styles.cancelBtn}>
                  Закрыть
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default DownloadSmet
